import { Mui } from "@osu/react-ui";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selector as selectorNotes, hasUncheckedChildren } from "../slices/administrativeNotes";
import { selector as selectorRubric } from "../slices/rubricValues";
import useCommonStyles from "../../Common/hooks/commonStyles";

// Bookkeeping fields held in the rubric-values slice that are NOT rubric items and
// must be ignored when evaluating completeness (see rubricValues slice).
const RUBRIC_NON_ITEM_KEYS = ["rubricKey"];

/**
 * Returns the rubric item entries (label -> selected choice value), excluding
 * bookkeeping fields such as `rubricKey`.
 */
function getRubricItemValues(rubricValues = {}) {
    return Object.entries(rubricValues ?? {})
        .filter(([key]) => !RUBRIC_NON_ITEM_KEYS.includes(key))
        .map(([, value]) => value);
}

/**
 * A displayed rubric is COMPLETE when it has at least one configured rubric item
 * AND every rubric item has a non-empty selected choice value.
 *
 * Requirement: rubric-completeness-requirements.md (FR-RC-1). Pure function so the
 * rule is deterministic and unit-testable (NFR-RC-1).
 */
export function isRubricComplete(rubricValues = {}) {
    const itemValues = getRubricItemValues(rubricValues);

    // No configured rubric items -> cannot be "selected to completion" (FR-RC-3).
    if (itemValues.length === 0) { return false; }

    // Every item must have a non-empty selection.
    return itemValues.every(value => value !== "" && value !== null && value !== undefined);
}

/** Inverse of isRubricComplete; convenience for validation call sites. */
export function isRubricIncomplete(rubricValues = {}) {
    return !isRubricComplete(rubricValues);
}

export function useSubmitValidationHandler(recommendationValue) {
    const notesState = useSelector(selectorNotes);
    const rubricValues = useSelector(selectorRubric);
    
    const missingRecommendation = !recommendationValue;
    const missingChildNodes = hasUncheckedChildren(notesState);
    const missingRubricValues = isRubricIncomplete(rubricValues);
    
    const [errorTexts, setErrorTexts] = useState([]);
    
    // Reset error text only if all errors go away
    const isMissingValues = missingRecommendation || missingChildNodes || missingRubricValues;
    useEffect(() => { setErrorTexts([]); }, [isMissingValues]);
    
    function checkForValidationErrors() {
        let errorTexts = [];
        if (missingRecommendation) { errorTexts.push("Please choose a recommendation before clicking submit."); }
        if (missingChildNodes) { errorTexts.push("You must select a subfield in Administrative Notes before clicking submit."); }
        if (missingRubricValues) { errorTexts.push("You must select a grade value for all rating items."); }
        
        if (errorTexts.length) { setErrorTexts(errorTexts); return false; }
        
        return true;
    }
    
    return { checkForValidationErrors, errorTexts };
}

export function SubmitValidationErrorMessage({ errorTexts }) {
    const classes = useCommonStyles();
    
    return (
        <Mui.Box aria-live="assertive">
            { errorTexts.map?.(errorText => ( <Mui.Typography aria-live="polite" key={errorText} sx={classes.errorClass} tabindex="0">{errorText}</Mui.Typography> )) }
        </Mui.Box>
    );
}